import { track, Identify, identify, setUserId } from '@amplitude/analytics-browser';
import LogManager from './LogManager';
import { ValidPropertyType } from '@amplitude/analytics-types';

const AMPLITUDE_LOCALSTORAGE_KEY = {
  HAS_SENT_WATCH_LIVE_EVENT: 'hasSentWatchLiveEvent',
}

export class AmplitudeEventManager {
  static appVersion: string | null = null;
  static mode: string | null = null;
  static jid: string | null = null;
  static signInChannel: string | null = null;
  static registerDate: string | null = null;
  static liveConnectionLength: number = -1;
  static relayServer: string | null = null;
  static liveConnectionTime: number = -1;
  static cameraId: string | null = null;
  static cameraName: string | null = null;

  setEventProperties(propertyName: string, value: ValidPropertyType) {
    const event = new Identify();
    event.set(propertyName, value)
    identify(event);
  }

  resetUser () {
    setUserId(undefined);
  }

  resetLiveProperties () {
    AmplitudeEventManager.liveConnectionLength = -1;
    AmplitudeEventManager.liveConnectionTime = -1;
    AmplitudeEventManager.relayServer = null;
  }

  setSignInChannel (signInChannel: string) {
    this.setEventProperties('sign_in_channel', signInChannel);
    AmplitudeEventManager.signInChannel = signInChannel;
  }

  setRegisterDate(registerDate: string) { 
    this.setEventProperties('register_date', registerDate);
    AmplitudeEventManager.registerDate = registerDate;
  }

  setUserProperties ({ uniqueId, subscriber, cameraCount, acOwner }: {
    uniqueId?: string,
    subscriber?: string,
    cameraCount?: number,
    acOwner?: string,
  }) {

    if (uniqueId) {
      setUserId(uniqueId);
    }

    if (subscriber) {
      this.setEventProperties('subscriber', subscriber);
    }
    
    if (cameraCount && acOwner) {
      console.log('Amplitude set user properties: ', `camera_count: ${cameraCount}, ac_owner: ${acOwner}`)
      this.setEventProperties('camera_count', cameraCount);
      this.setEventProperties('ac_owner', acOwner);
    }
  }

  setDefaultEventProperties ({ appVersion, mode, jid }: { appVersion?: string, mode?: string, jid?: string }) {
    if (appVersion) {
      AmplitudeEventManager.appVersion = appVersion;
    }
    
    if (mode) {
      AmplitudeEventManager.mode = mode;
    }

    if (jid) {
      AmplitudeEventManager.jid = jid;
    }
  }

  setUserUniqueId ({ uniqueId }: { uniqueId?: string }) {
    setUserId(uniqueId);
  }

  sendEvent (eventName: string, properties?: Record<string, unknown>) {
    // after sign in will set appVerions, mode and jid
    const combinedProperties = {
      ...properties,
      app_version: AmplitudeEventManager.appVersion,
      mode: AmplitudeEventManager.mode,
      jid: AmplitudeEventManager.jid
    }
    track(eventName, combinedProperties);
    const logManager = LogManager.getInstance();
    logManager.log(`[AmplitudeEvent] ${eventName}`, combinedProperties);
  }

  signInPageLanded (appVersion?: string) {
    const logManager = LogManager.getInstance();
    logManager.log('[AmplitudeEvent] pageview: landing page', { app_version: appVersion, mode: AmplitudeEventManager.mode });
    track('pageview: landing page', { app_version: appVersion, mode: AmplitudeEventManager.mode });
  }

  signInSuccess ({ success, method, isSignup = false }: { success: boolean, method: string, isSignup?: boolean }) {
    const signInChannel = method || AmplitudeEventManager.signInChannel;
    const properties = {
      success,
      method: signInChannel,
      is_signup: isSignup
    }
    this.sendEvent('onboard: login', properties);
  }

  upgradeToSubscriber ({ productId, success }: { productId: string, success: boolean }) {
    const properties = { product_id: productId, success };
    this.sendEvent('convert: upgrade to subscriber', properties);
  }

  paymentPageLanded ({ from, paymentUrl }: { from: string, paymentUrl: string }) {
    const properties = {
      from,
      url: paymentUrl.replace(/^https:\/\//, '')
    }
    this.sendEvent('pageview: subscription', properties);
  }

  watchRecordedVideo ({ cameraId, cameraName, playerType, byAlert, timestamp }: {
    cameraId: string,
    cameraName: string,
    playerType: string,
    byAlert: boolean,
    timestamp: number
  }) {
    const properties = {
      cam_id: cameraId,
      cam_name: cameraName,
      player_type: playerType,
      by_alert: byAlert,
      timestamp
    }
    this.sendEvent('viewer: watch recorded video', properties);
  }

  watchLive ({ reason, hasBeenReconnected }: { reason: string, hasBeenReconnected?: boolean }) {
    const properties = {
      cam_id: AmplitudeEventManager.cameraId,
      cam_name: AmplitudeEventManager.cameraName,
      connection_time:
        AmplitudeEventManager.liveConnectionTime > 0
          ? AmplitudeEventManager.liveConnectionTime
          : -1,
      by_alert: false,
      length: AmplitudeEventManager.liveConnectionLength,
      reason,
      relay: AmplitudeEventManager.relayServer,
      viewer_count: 1, // webviewer not support multiple viewer,
      has_been_reconnected: hasBeenReconnected || false
    };
    const hasSentWatchLiveEvent = localStorage.getItem(AMPLITUDE_LOCALSTORAGE_KEY.HAS_SENT_WATCH_LIVE_EVENT);
    if (!hasSentWatchLiveEvent) {
      this.sendEvent('viewer: watch live', properties);
      localStorage.setItem(AMPLITUDE_LOCALSTORAGE_KEY.HAS_SENT_WATCH_LIVE_EVENT, 'true');
    }
  }

  setLiveCameraInfo (cameraId: string, cameraName: string) {
    AmplitudeEventManager.cameraId = cameraId;
    AmplitudeEventManager.cameraName = cameraName;
  }

  registeredCamera ({ success }: { success: boolean }) {
    const properties = {
      success
    }
    this.sendEvent('camera: registered a camera', properties);
  }

  adBlockerDetected () {
    this.sendEvent('pageview: ads blocker detected', undefined);
  }

  setLiveConnectionLength(length: number) {
    AmplitudeEventManager.liveConnectionLength = length;
  }

  setLiveConnectionTime(time: number) {
    AmplitudeEventManager.liveConnectionTime = time;
  }

  setRelayServer(server: string) {
    AmplitudeEventManager.relayServer = server;
  }
}

const amplitudeEventManager = new AmplitudeEventManager();

export default amplitudeEventManager;